"use client"

import { Link } from "@inertiajs/react"
import { Share03 } from "@untitledui/icons"

import { Avatar } from "@/components/ui/avatar"
import { Tooltip } from "@/components/ui/tooltip"
import { profilePath } from "@/routes"
import type { BriefContributor } from "@/types"

interface ContributorsStripProps {
  featured: BriefContributor[]
}

const ROLE_COPY: Record<string, string> = {
  lead_curator: "Lead curator",
  clinical_advisor: "Clinical advisor",
  featured_contributor: "Featured contributor",
  moderator: "Moderator",
  contributor: "Contributor",
}

// The quiet credits card beside the feed: featured contributors only.
// Track contributors already appear where their work is — in the feed
// attribution rows — so the old sticky sidebar's second list is gone
// along with the stickiness.
export function ContributorsStrip({ featured }: ContributorsStripProps) {
  if (featured.length === 0) return null

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-tertiary text-xs font-semibold tracking-wide uppercase">
        Featured contributors
      </h3>
      <ul className="flex flex-col gap-3">
        {featured.map((contributor) => (
          <ContributorRow key={contributor.id} contributor={contributor} />
        ))}
      </ul>
    </section>
  )
}

function ContributorRow({ contributor }: { contributor: BriefContributor }) {
  const roleCopy = ROLE_COPY[contributor.role] ?? contributor.role
  const profileHref =
    contributor.user?.has_public_profile && contributor.user?.slug
      ? profilePath(contributor.user.slug)
      : null

  const body = (
    <>
      <Avatar
        name={contributor.display_name}
        avatarUrl={contributor.user?.avatar_url}
        size="sm"
      />
      <div className="flex flex-col">
        <div className="text-primary inline-flex items-center gap-1 text-sm font-medium">
          <span className="text-primary text-sm font-medium">
            {contributor.display_name}
          </span>
          <Share03 aria-hidden="true" className="size-3 shrink-0" />
        </div>

        <span className="text-tertiary text-xs">{roleCopy}</span>
      </div>
    </>
  )

  return (
    <li>
      <Tooltip
        title={roleCopy}
        description={contributor.user?.tagline ?? undefined}
        placement="left"
      >
        {profileHref ? (
          <Link
            href={profileHref}
            className="hover:bg-secondary_subtle -mx-1 flex items-center gap-3 rounded-md p-1 transition-colors"
          >
            {body}
          </Link>
        ) : (
          <div className="flex items-center gap-3" tabIndex={0}>
            {body}
          </div>
        )}
      </Tooltip>
    </li>
  )
}
